import API from 'api/helpers/client';
import type { AxiosResponse } from 'axios';
import { displayBackendErrorMessage } from 'components/DisplayBackEndErrorMessage';
import { identifyFullStory } from 'helpers/utils.ts';
import { submissionSelector, underwritingQuestionsSelector } from './submission.selectors';
import type {
  CreateSubmission,
  IAddExposureOrGroup,
  IAddManuallyEnteredLocationExposure,
  IDeleteExposureOrGroup,
  IExcludeWcRuleResult,
  ISendSubmisssionLinkEmail,
  ISubmissionPriceQueries,
  IUpdateEarthquakeCoverage,
  IUpdateExposureOrGroup,
  IUpdateManuallyEnteredLocationExposure,
  IUpdatePaymentPlan,
  IUpdatePolicyData,
  IUpdateUwQuestions,
  Submission,
  SubmissionPrice,
  TransformedSubmission,
  UnderwritingQuestionsResponse,
  UpdateSubmission,
} from './submission.types';

export const fetchUnderwritingQuestions = async (
  id: string,
): Promise<UnderwritingQuestionsResponse> => {
  const { data } = await API.get(`consumer-submission/${id}/underwriting-questions-forms`);

  return underwritingQuestionsSelector(data);
};

export const createQuoteFromSubmission = async (id: string): Promise<Submission> =>
  (await API.post(`consumer-submission/${id}/create-quote-from-submission`)).data;

export const createSubmission = async (payload: CreateSubmission): Promise<Submission> =>
  (await API.post(`consumer-submission`, payload)).data;

export const updateSubmission = async (payload: UpdateSubmission): Promise<Submission> =>
  (await API.patch(`consumer-submission/${payload.id}`, payload)).data;

export const updatePaymentPlan = async (payload: IUpdatePaymentPlan): Promise<Submission> =>
  (await API.post(`consumer-submission/${payload.submissionId}/select-payment-plan`, payload.body))
    .data;

export const getSubmission = async (id: string): Promise<TransformedSubmission> => {
  const { data } = await API.get<Submission>(`consumer-submission/${id}`);

  if (data.email) {
    identifyFullStory(data.email);
  }

  return submissionSelector(data);
};

export const updatePolicyData = async (payload: IUpdatePolicyData): Promise<Submission> =>
  (await API.patch(`consumer-submission/${payload.id}/policy-fields`, payload.body)).data;

export const updateUwQuestions = async (payload: IUpdateUwQuestions): Promise<Submission> =>
  (await API.patch(`consumer-submission/${payload.id}/underwriting-questions`, payload.body)).data;

// #region exposures
export const addExposure = async (payload: IAddExposureOrGroup): Promise<Submission> =>
  (await API.post(`consumer-submission/${payload.submissionId}/exposures`, payload.body)).data;

export const updateExposure = async (payload: IUpdateExposureOrGroup): Promise<Submission> =>
  (await API.patch(`consumer-submission/${payload.submissionId}/exposures`, payload.body)).data;

export const addManuallyEnteredLocationExposure = async (
  payload: IAddManuallyEnteredLocationExposure,
): Promise<void> =>
  (await API.post(`consumer-submission/${payload.submissionId}/fill-address`, payload.body)).data;

export const updateManuallyEnteredLocationExposure = async (
  payload: IUpdateManuallyEnteredLocationExposure,
): Promise<void> =>
  (await API.patch(`consumer-submission/${payload.submissionId}/fill-address`, payload.body)).data;

export const bulkUpdateExposure = async (payload: IUpdateExposureOrGroup): Promise<Submission> =>
  (await API.patch(`consumer-submission/${payload.submissionId}/bulk-exposures/`, payload.body))
    .data;

export const deleteExposure = async (payload: IDeleteExposureOrGroup): Promise<Submission> =>
  (
    await API.delete(`consumer-submission/${payload.submissionId}/exposures`, {
      data: payload.body,
    })
  ).data;
// #endregion

// #region group fields
export const addGroupField = async (payload: IAddExposureOrGroup): Promise<Submission> =>
  (await API.post(`consumer-submission/${payload.submissionId}/group-fields`, payload.body)).data;

export const bulkAddGroupField = async (payload: IUpdateExposureOrGroup): Promise<Submission> =>
  (await API.post(`consumer-submission/${payload.submissionId}/bulk-group-fields/`, payload.body))
    .data;

export const updateGroupField = async (payload: IUpdateExposureOrGroup): Promise<Submission> =>
  (await API.patch(`consumer-submission/${payload.submissionId}/group-fields`, payload.body)).data;

export const bulkUpdateGroupField = async (payload: IUpdateExposureOrGroup): Promise<Submission> =>
  (await API.patch(`consumer-submission/${payload.submissionId}/bulk-group-fields/`, payload.body))
    .data;

export const bulkDeleteGroupField = async (payload: IDeleteExposureOrGroup): Promise<Submission> =>
  (
    await API.delete(`consumer-submission/${payload.submissionId}/bulk-group-fields/`, {
      data: payload.body,
    })
  ).data;

export const deleteGroupField = async (payload: IDeleteExposureOrGroup): Promise<Submission> =>
  (
    await API.delete(`consumer-submission/${payload.submissionId}/group-fields`, {
      data: payload.body,
    })
  ).data;
// #endregion

export const updateEarthquakeCoverage = async (payload: IUpdateEarthquakeCoverage) =>
  (
    await API.patch(`consumer-submission/${payload.submissionId}/earthquake-coverage`, {
      pol_eq_excluded: payload.pol_eq_excluded,
    })
  ).data;

export const fetchSubmissionPrice = async (
  id: string,
  query?: ISubmissionPriceQueries,
): Promise<SubmissionPrice> => {
  let response;

  try {
    response = await API.get(`consumer-submission/${id}/price`, {
      params: query,
    });
  } catch (err) {
    displayBackendErrorMessage(err);
  }

  return response.data;
};

export const sendSubmissionLinkEmail = async (payload: ISendSubmisssionLinkEmail): Promise<void> =>
  (await API.post(`consumer-submission/${payload}/save/`)).data;

export const sendSubmissionAccessLinkEmail = async (email: string): Promise<{ message: string }> =>
  (await API.post(`consumer-submission/send-access-link/`, { email })).data;

export const invalidatePreviousSubmissions = async (email: string): Promise<{ message: string }> =>
  (await API.post(`consumer-submission/invalidate-submissions/`, { email })).data;

export const updatePaperlessOptIn = async ({
  submissionId,
  paperlessOptIn,
}: {
  submissionId: string;
  paperlessOptIn: boolean;
}): Promise<{ paperless_opt_in: boolean }> => {
  const { data } = await API.patch(`consumer-submission/${submissionId}/paperless-opt-in/`, {
    paperless_opt_in: paperlessOptIn,
  });
  return data;
};

export const excludeWcRuleResult = async (submissionId: string): Promise<IExcludeWcRuleResult> => {
  let response: AxiosResponse<IExcludeWcRuleResult> | undefined;

  try {
    response = await API.get(`consumer-submission/${submissionId}/exclude-wc-rule-result/`);
  } catch (error) {
    displayBackendErrorMessage(error);
  }

  // If error, return false by default so it won't block nor affect the quote flow
  return response?.data ?? { has_exclude_wc_rule: false };
};
